import { useState, useEffect } from "react";

export const useLessonData = (open, isAuthenticated, user, lessonData) => {
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [passes, setPasses] = useState([]);
  const [canSignData, setCanSignData] = useState({ 
    available: true, 
    has_pass: false, 
    is_on_waitlist: false, 
    waitlist_position: null,
    has_unpaid_debt: false,
    pass_expired: false,
    debt_amount: 0,
    debt_count: 0,
    debt_details: []
  });

  const fetchCanSignData = async () => {
    if (!isAuthenticated || !lessonData.lesson_id) return;

    const idToken = await user.getIdToken();
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/can_sign_to_lesson/${lessonData.lesson_id}`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      const data = await res.json();
      console.log('[DEBUG] can_sign_to_lesson response for lesson', lessonData.lesson_id, ':', data);
      console.log('[DEBUG] has_pass from can_sign_to_lesson:', data.has_pass);
      
      setCanSignData({
        available: data.available,
        has_pass: data.has_pass,
        is_on_waitlist: data.is_on_waitlist || false,
        waitlist_position: data.waitlist_position || null,
        has_unpaid_debt: data.has_unpaid_debt || false,
        pass_expired: data.pass_expired || false,
        debt_amount: data.debt_amount || 0,
        debt_count: data.debt_count || 0,
        debt_details: data.debt_details || [],
        // Duo/trio: someone already reserved the whole lesson.
        duo_booked: data.duo_booked || false,
        // Duo/trio with single spots: which mode is still open and at what price.
        whole_available: data.whole_available ?? data.available,
        spot_available: data.spot_available || false,
        free_spots: data.free_spots ?? null,
        spot_price: data.spot_price ?? null,
        whole_price: data.whole_price ?? null,
        has_whole_pass: data.has_whole_pass || false,
        has_spot_pass: data.has_spot_pass || false,
      });
    } catch (error) {
      console.error('[DEBUG] Error checking lesson availability:', error);
      setCanSignData({
        available: false,
        has_pass: false,
        is_on_waitlist: false,
        waitlist_position: null,
        has_unpaid_debt: false,
        pass_expired: false,
        debt_amount: 0,
        debt_count: 0,
        debt_details: []
      });
    }
  };

  const fetchPasses = async () => {
    if (!isAuthenticated || !lessonData.lesson_id) return;

    const idToken = await user.getIdToken();
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/pass/mine?active=true&lesson_id=${lessonData.lesson_id}`,
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      const data = await res.json();
      console.log('[DEBUG] fetchPasses response for lesson', lessonData.lesson_id, ':', data);
      
      if (Array.isArray(data.objects)) {
        console.log('[DEBUG] Total passes returned:', data.objects.length);
        
        // Check if any event pass is available for this lesson
        const eventPasses = data.objects.filter((pass) => pass.is_event_pass);
        console.log('[DEBUG] Event passes found:', eventPasses.length, eventPasses);
        
        // If there are event passes, show ONLY event passes (they take priority)
        if (eventPasses.length > 0) {
          console.log('[DEBUG] Using event passes:', eventPasses);
          setPasses(eventPasses);
          return;
        }
        
        // Check if any pass has unlimited lessons for this lesson type
        const unlimitedPasses = data.objects.filter((pass) =>
          pass.lesson_categories_for_pass.some(
            (category) => category.is_unlimited
          )
        );
        console.log('[DEBUG] Unlimited passes found:', unlimitedPasses.length);

        // If there are unlimited passes, show only those; otherwise show all passes
        const finalPasses = unlimitedPasses.length > 0 ? unlimitedPasses : data.objects;
        console.log('[DEBUG] Final passes to display:', finalPasses.length, finalPasses);
        setPasses(finalPasses);
      } else {
        console.log('[DEBUG] No passes array in response');
        setPasses([]);
      }
    } catch (error) {
      console.error('[DEBUG] Error fetching passes:', error);
      setPasses([]);
    }
  };

  const refreshData = async () => {
    setIsDataLoading(true);
    await fetchCanSignData();
    await fetchPasses();
    setIsDataLoading(false);
  };

  useEffect(() => {
    if (open && isAuthenticated) {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated]);

  return {
    isDataLoading,
    passes,
    canSignData,
    fetchCanSignData,
    fetchPasses,
    refreshData
  };
};
